import React, { useRef } from "react";

import { Grid, Card, Button, Alert, IconButton, Collapse } from "@mui/material";
import { Close as CloseIcon, AddCircle as AddCircleIcon } from "components/icons";
import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { blue, green } from '@mui/material/colors';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { addActivityToBackend } from "app/services/member.service";
import { getDateInFormat } from "utils/dateUtil";
import * as AppConstants from "AppConstants";
import { checkUserHasMemberRole } from "app/services/app.state.service";
import { handleAxiosError } from "app/services/app.error.handler";

import "app/components/team-activities/AddActivityForm.css";
import { isEmpty } from "utils/validationUtil";
import { validateActivity } from "utils/activityValidationUtil";

export const AddActivityForm = ({ownerId, refreshActivitiesDataHandler}) => {
    const [activityStartDate, setActivityStartDate] = React.useState(new Date());
    const [activityEndDate, setActivityEndDate] = React.useState(new Date());
    const [activityClientTicketRef, setActivityClientTicketRef] = React.useState("");
    const [activityDescription, setActivityDescription] = React.useState("");
    const [activityHoursSpent, setActivityHoursSpent] = React.useState(8);
    const [activityStatus, setActivityStatus] = React.useState(AppConstants.DEFAULT_ACTIVITY_STATUS);
    const [message, setMessage] = React.useState("");
    const [open, setOpen] = React.useState(false);
    const [severity, setSeverity] = React.useState("info"); // "error" or "warning" or "info" or "success"
    const [fieldErrors, setFieldErrors] = React.useState({});
    const hoursInputRef = useRef(null);

    const clearAddActivityForm = () => {
        setActivityClientTicketRef("");
        setActivityHoursSpent("");
        setActivityDescription("");
        setActivityStatus(AppConstants.DEFAULT_ACTIVITY_STATUS)
        document.getElementById("addActivityClientRef").value = "";
        document.getElementById("addActivityDescription").value = "";
        document.getElementById("addActivityHoursSpent").value = "";
        document.getElementById("addActivityStatus").value = AppConstants.DEFAULT_ACTIVITY_STATUS;
        setFieldErrors({});
    }

    const addActivityClickHandler = async () => {
        const activityObject = {
            description: activityDescription,
            clientTicketRef: activityClientTicketRef,
            ownerId: ownerId,
            startDate: getDateInFormat(activityStartDate),
            endDate: getDateInFormat(activityEndDate),
            status: activityStatus,
            hoursSpent: activityHoursSpent === "" ? NaN : parseFloat(activityHoursSpent),
        };

        const { isValid, fieldErrors: errs } = validateActivity(activityObject);
        if (!isValid) {
            setFieldErrors(errs);
            if (errs.hoursSpent && hoursInputRef.current) {
                hoursInputRef.current.focus();
            }
            // TODO: render inline errors for the remaining fields. Until then, surface any
            // non-Hours failures in the bottom Alert so the user isn't left without feedback.
            const otherErrors = Object.entries(errs)
                .filter(([key]) => key !== "hoursSpent")
                .map(([, msg]) => msg);
            if (otherErrors.length > 0) {
                setErrorAction(otherErrors.join(" "));
            }
            return;
        }

        setFieldErrors({});
        await addActivityInvoker(activityObject);
    };

    // Sets error message useState
    // Sets open to true for alert box
    // Sets severity to "error"

    const setErrorAction = (errorMessage) => {
        setMessage(errorMessage);
        setOpen(true);
        setSeverity("error")
    }

    const setSuccessAction = (message) => {
        setMessage(message);
        setOpen(true);
        setSeverity("success")
    }


    const addActivityInvoker = async (activityObject) => {
        await addActivityToBackend(activityObject)
        .then(data => {
            setSuccessAction("Activity added successfully");
            clearAddActivityForm();
            refreshActivitiesDataHandler();
        })
        .catch(error=>{
            handleAxiosError(error);     
        });
    };
 
    if(!checkUserHasMemberRole()) {
        return <div>You do not have access to this functionality.</div>;
    }

    return (
        <Grid item xs={12} sm={12} md={12}>
            <Card>
                <MDBox
                    mx={2}
                    mt={-3}
                    py={1}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                >
                    <MDTypography variant="h6" color="white">
                        Add Activity
                    </MDTypography>
                </MDBox>
                <MDBox component="form" mt={3} p={2} >
                    <Grid
                        container 
                        spacing={2}
                        className="add-activity-form-grid-container"
                    >
                        <Grid
                            container
                            spacing={2}
                        >
                            <Grid item xs={3} sm={3} md={3}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        disableFuture
                                        label="Start Date"
                                        value={activityStartDate}
                                        onChange={(newStartDate) => {
                                            setActivityStartDate(newStartDate);
                                            setActivityEndDate(newStartDate)
                                        }}
                                        renderInput={(params) => <TextField {...params} />}
                                        inputFormat={AppConstants.DATE_FORMAT_DISPLAY}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={3} sm={3} md={3}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        disableFuture
                                        disabled={true}
                                        label="End Date"
                                        value={activityEndDate}
                                        onChange={(newEndDate) => {
                                            setActivityEndDate(newEndDate);
                                            // setToDate(newEndDate);
                                        }}
                                        renderInput={(params) => <TextField {...params} />}
                                        inputFormat={AppConstants.DATE_FORMAT_DISPLAY}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={5} sm={5} md={5}>
                                <TextField
                                    sx={{
                                        width: "420px"
                                    }}
                                    id="addActivityDescription"
                                    label="Activity description"
                                    placeholder="Activity description"
                                    onChange={(descChangedEvent) => setActivityDescription(descChangedEvent.target.value)}
                                    helperText="*Required | Up to 255 characters"
                                    multiline
                                    maxRows={2}
                                />
                            </Grid>
                        </Grid>

                        <Grid
                            container
                            spacing={1}
                            py={1}
                            px={1}
                        >
                            <Grid item xs={3} sm={3} md={3}>
                                <TextField
                                    sx={{
                                        width: "230px"
                                    }}
                                    id="addActivityClientRef"
                                    label="Client Ticket Ref"
                                    placeholder="Jira reference"
                                    defaultValue=""
                                    variant="outlined"
                                    helperText=""
                                    onChange={(clientRefEvent) => {
                                        setActivityClientTicketRef(clientRefEvent.target.value)
                                    } }
                                />
                            </Grid>
                            <Grid item xs={3} sm={3} md={3}>
                                <TextField
                                    sx={{ width: "250px" }}
                                    id="addActivityHoursSpent"
                                    label="Hours Spent"
                                    type="number"
                                    placeholder="Hours Spent"
                                    inputRef={hoursInputRef}
                                    value={activityHoursSpent}
                                    // Prevent the mouse wheel from silently incrementing/
                                    // decrementing the number input while it has focus. Blurring
                                    // lets the scroll fall through to the popup instead.
                                    onWheel={(e) => e.target.blur()}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        setActivityHoursSpent(raw);
                                        const parsed = raw === "" ? NaN : parseFloat(raw);
                                        const candidate = {
                                            description: activityDescription,
                                            clientTicketRef: activityClientTicketRef,
                                            ownerId: ownerId,
                                            startDate: getDateInFormat(activityStartDate),
                                            endDate: getDateInFormat(activityEndDate),
                                            status: activityStatus,
                                            hoursSpent: parsed,
                                        };
                                        const { fieldErrors: errs } = validateActivity(candidate);
                                        setFieldErrors((prev) => ({ ...prev, hoursSpent: errs.hoursSpent }));
                                    }}
                                    error={Boolean(fieldErrors.hoursSpent)}
                                    helperText={fieldErrors.hoursSpent ?? "Required. Greater than 0, up to 12."}
                                    slotProps={{ htmlInput: { min: 0.1, max: 12, step: 0.5 } }}
                                />
                            </Grid>
                            <Grid item xs={2} sm={2} md={2}>
                                <TextField
                                    sx={{
                                        width: "170px"
                                    }}
                                    select
                                    // id="Status"
                                    label="Status"
                                    id="addActivityStatus"
                                    defaultValue="IN_PROGRESS"
                                    helperText="*Required"
                                    onChange={(statuChangedEvent) => setActivityStatus(statuChangedEvent.target.value)}
                                    SelectProps={{
                                        native: true,
                                    }}
                                >
                                    {AppConstants.ACTIVITY_STATUSES.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={2} sm={3} md={3}>
                                <Button 
                                    onClick={addActivityClickHandler}
                                    sx={{width:150, m: 0, ml:3}}
                                    variant="contained" 
                                    color="success"
                                    endIcon={<AddCircleIcon id="add-activity-for-member" fontSize="large" sx={{
                                            // fontSize: 40,
                                            palette: {
                                                primary: blue,
                                                secondary: green,
                                            },
                                        }} 
                                    />}
                                >
                                    Add
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Collapse in={open}>
                        <Alert
                        action={
                            <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => {
                                setOpen(false);
                            }}
                            >
                            <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                        severity={severity}
                        sx={{ mb: 2 }}
                        >
                        {message}
                        </Alert>
                    </Collapse>
                </MDBox>
            </Card>
        </Grid>
    );

}